import React from 'react'

import {
  Accordion,
  AlignItems,
  Body2,
  Bold,
  BorderStyle,
  Box,
  Color,
  Display,
  FontFamily,
  Heading2,
  Heading3,
  Icon,
  IconVariant,
  JustifyContent,
  Text,
} from '@gousto-internal/citrus-react'
import { LandingPageButton } from '../../../../components'
import { HowGoustoFoodBoxesWorkTypes } from '../../../../types'
import { FoodBoxPageSections, TargetUri, TrackingActions } from '../../../../types/tracking'

const renderAccordionHeader = (question: string) => {
  const AccordionHeader = (isExpanded: boolean) => (
    <Box
      display={Display.Flex}
      justifyContent={JustifyContent.SpaceBetween}
      alignItems={AlignItems.Center}
    >
      <Heading3 color={Color.White} fontFamily={FontFamily.Bold} size={6}>
        {question}
      </Heading3>

      <Text color={Color.White} size={15}>
        <Icon
          variant={IconVariant.Inherit}
          name={isExpanded ? 'chevron_large_up' : 'chevron_large_down'}
        />
      </Text>
    </Box>
  )

  return AccordionHeader
}

export const FoodBoxFAQSection = ({ startOnboarding }: HowGoustoFoodBoxesWorkTypes) => (
  <Box paddingH={[8, 20]} paddingV={16} display={Display.Block} bg={Color.ColdGrey_600}>
    <Box display={Display.Flex} justifyContent={JustifyContent.Center}>
      <Box maxWidth="1033px" width="100%">
        <Box paddingBottom={2}>
          <Heading2 color={Color.White}>Gousto food boxes FAQs</Heading2>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="faq-0"
            title={renderAccordionHeader('Is a Gousto food box actually worth it?')}
          >
            <Box paddingBottom={2}>
              <Body2 color={Color.White} size={4}>
                Yes, chef! You simply pick the recipes you love to arrive on a day that suits you,
                and we send all the top-quality, perfectly pre-portioned ingredients straight to
                your door. No mealtime mayhem, no food waste and no shopping stress - just tasty
                meals made easy.
              </Body2>
            </Box>
          </Accordion>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="faq-1"
            title={renderAccordionHeader('Do I have to get a food box every single week?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Nope. Our food box subscription is super flexible. You can pause, skip or stop
                deliveries anytime from the app or website. you’re totally in charge. No awkward
                break-up needed.
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="faq-2" title={renderAccordionHeader('What does a Gousto food box cost?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                The price of your recipe box depends on the number of recipes you choose and how
                many people you’re catering for. The more you order, the less you’ll pay per
                portion, with prices starting at £3.09 per portion. If you’d like more information
                on prices, check out our guide to see how much a{' '}
                <LandingPageButton
                  startOnboarding={startOnboarding}
                  asLink
                  sectionName={FoodBoxPageSections.FAQ_SECTION}
                  target={TargetUri.BOX_PRICES}
                  action={TrackingActions.BOX_PRICING_NAVIGATION_CLICKED}
                  whiteText
                >
                  <Bold>recipe box costs.</Bold>
                </LandingPageButton>{' '}
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="faq-3" title={renderAccordionHeader('Are Gousto food boxes sustainable?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Very. From recyclable boxes and smart packaging to clever portioning that cuts down
                on waste, we’re all about good food that’s good for the planet, too.
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="faq-4" title={renderAccordionHeader('Do you deliver to my house?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                We deliver to any UK or Republic of Ireland address you fancy - home, office,
                treehouse (okay, maybe not that last one, but you get the idea).
              </Body2>
            </Box>
          </Accordion>
        </Box>

        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="faq-5"
            title={renderAccordionHeader('Can I take a break from food box deliveries?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Absolutely. Skip a week, pause your subscription—whatever suits you. Do it all with
                a few taps from your device, no drama.
              </Body2>
            </Box>
          </Accordion>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion
            id="faq-6"
            title={renderAccordionHeader('Why is Gousto the best food box in the UK?')}
          >
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                We’ve got the biggest menu, the best value, and top-notch ingredients that make
                every meal a winner. Basically, if dinner was a competition, you’d be nailing it.
              </Body2>
            </Box>
          </Accordion>
        </Box>
        <Box
          borderColor={Color.White}
          borderStyle={BorderStyle.Solid}
          borderBottomWidth={0.5}
          paddingV={5}
          width="100%"
        >
          <Accordion id="faq-7" title={renderAccordionHeader('What size boxes does Gousto have?')}>
            <Box paddingBottom={2}>
              <Body2 size={4} color={Color.White}>
                Did you know that we’re the first recipe box in Britain to serve 1-5 people? Our
                food boxes are made to suit a number of households, as we understand that every home
                looks different. Whether you’re a busy family of 5, a couple looking to shake up
                their dinners, or you live alone and want to cut down on food waste, there’s a box
                for you.
              </Body2>
            </Box>
          </Accordion>
        </Box>
      </Box>
    </Box>
  </Box>
)
